import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowRight,
  BookOpen,
  Boxes,
  Command,
  MousePointer2,
  Sparkles,
  X
} from 'lucide-react';
import { useScene } from '@/hooks/useScene';
import { useUiStateStore } from '@/stores/uiStateStore';
import type { Mode } from '@/types';

export const ONBOARDING_STORAGE_KEY = 'flowi:onboarding:milestone-4:v1';

interface EditorOnboardingProps {
  isHelpOpen: boolean;
  isReadOnly: boolean;
  onCloseHelp: () => void;
  onOpenHelp: () => void;
  onOpenLibrary: () => void;
}

const shortcuts = [
  ['Space', 'Temporarily pan the canvas'],
  ['Ctrl/Cmd + A', 'Select everything'],
  ['Ctrl/Cmd + C / V', 'Copy and paste'],
  ['Ctrl/Cmd + D', 'Duplicate selection'],
  ['Ctrl/Cmd + G', 'Group selection'],
  ['Ctrl/Cmd + Shift + G', 'Ungroup selection'],
  ['Arrow keys', 'Move one tile'],
  ['Shift + Arrow keys', 'Move five tiles'],
  ['Delete', 'Delete selection'],
  ['Esc', 'Return to Select and clear selection']
] as const;

export const getModeHint = (mode: Mode): string | null => {
  switch (mode.type) {
    case 'PLACE_ICON': return 'Choose a component, then click or drop it on the canvas.';
    case 'RECTANGLE.DRAW': return 'Click and drag across the grid to draw an area.';
    case 'CONNECTOR': return 'Drag between two points, or connect two elements.';
    case 'TEXTBOX': return 'Click the canvas to place text. Double-click text to edit it later.';
    case 'PAN': return 'Drag to pan. Release Space to return to your previous tool.';
    case 'DRAG_ITEMS': return 'Release to place. Exact alignment guides appear as you move.';
    default: return null;
  }
};

export const EditorOnboarding: React.FC<EditorOnboardingProps> = ({
  isHelpOpen,
  isReadOnly,
  onCloseHelp,
  onOpenHelp,
  onOpenLibrary
}) => {
  const scene = useScene();
  const mode = useUiStateStore((state) => state.mode);
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(ONBOARDING_STORAGE_KEY) === 'complete';
  });
  const isEmpty = scene.items.length === 0 &&
    scene.connectors.length === 0 &&
    scene.rectangles.length === 0 &&
    scene.textBoxes.length === 0;
  const modeHint = useMemo(() => getModeHint(mode), [mode]);
  const closeHelpRef = useRef<HTMLButtonElement>(null);
  const helpDialogRef = useRef<HTMLElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const completeOnboarding = useCallback(() => {
    window.localStorage.setItem(ONBOARDING_STORAGE_KEY, 'complete');
    setDismissed(true);
  }, []);

  useEffect(() => {
    if (!isEmpty && !dismissed) completeOnboarding();
  }, [completeOnboarding, dismissed, isEmpty]);

  useEffect(() => {
    if (!isHelpOpen) return;
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    closeHelpRef.current?.focus();
    return () => {
      if (previousFocusRef.current?.isConnected) previousFocusRef.current.focus();
      previousFocusRef.current = null;
    };
  }, [isHelpOpen]);

  return (
    <>
      {!isReadOnly && isEmpty && !dismissed && mode.type === 'CURSOR' && (
        <section
          data-editor-ui="true"
          aria-label="Getting started"
          className="absolute left-1/2 top-1/2 z-[12] w-[min(34rem,calc(100%-3rem))] -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-blue-200 bg-white/95 p-6 shadow-2xl backdrop-blur dark:border-blue-900 dark:bg-slate-900/95"
        >
          <button
            type="button"
            className="absolute right-3 top-3 rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
            onClick={completeOnboarding}
            aria-label="Dismiss getting started"
          >
            <X size={18} />
          </button>
          <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-blue-100 text-blue-600 dark:bg-blue-950 dark:text-blue-300">
            <Sparkles size={22} />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Build your first diagram</h2>
          <p className="mt-2 max-w-md text-sm leading-6 text-slate-600 dark:text-slate-300">
            Drag components from the library, connect them, and arrange everything directly on the isometric grid.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => { completeOnboarding(); onOpenLibrary(); }}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
            >
              <Boxes size={17} /> Browse components <ArrowRight size={15} />
            </button>
            <button
              type="button"
              onClick={onOpenHelp}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              <Command size={17} /> View shortcuts
            </button>
          </div>
          <div className="mt-5 grid gap-2 border-t border-slate-200 pt-4 text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400 sm:grid-cols-3">
            <span>Drag to move</span><span>Right-click for actions</span><span>Hold Space to pan</span>
          </div>
        </section>
      )}

      {!isReadOnly && modeHint && (
        <div
          data-editor-ui="true"
          role="status"
          className="pointer-events-none absolute bottom-5 left-1/2 z-[12] flex -translate-x-1/2 items-center gap-2 rounded-full border border-slate-200 bg-white/90 px-4 py-2 text-xs font-medium text-slate-600 shadow-lg backdrop-blur dark:border-slate-700 dark:bg-slate-900/90 dark:text-slate-300"
        >
          <MousePointer2 size={14} className="text-blue-500" /> {modeHint}
        </div>
      )}

      {isHelpOpen && (
        <div
          data-editor-ui="true"
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm"
          onMouseDown={(event) => { if (event.target === event.currentTarget) onCloseHelp(); }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              event.stopPropagation();
              onCloseHelp();
            } else if (event.key === 'Tab') {
              const focusable = Array.from(helpDialogRef.current?.querySelectorAll<HTMLElement>(
                'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
              ) ?? []);
              if (focusable.length === 0) return;
              const first = focusable[0];
              const last = focusable[focusable.length - 1];
              if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
              } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
              }
            }
          }}
        >
          <section
            ref={helpDialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="editor-help-title"
            className="max-h-[min(46rem,calc(100vh-2rem))] w-full max-w-3xl overflow-y-auto rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900"
          >
            <header className="sticky top-0 flex items-center justify-between border-b border-slate-200 bg-white/95 px-6 py-4 backdrop-blur dark:border-slate-700 dark:bg-slate-900/95">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-blue-100 p-2 text-blue-600 dark:bg-blue-950 dark:text-blue-300"><BookOpen size={20} /></div>
                <div><h2 id="editor-help-title" className="font-semibold text-slate-900 dark:text-white">Flowi quick guide</h2><p className="text-xs text-slate-500 dark:text-slate-400">Press ? to open this guide anytime</p></div>
              </div>
              <button type="button" ref={closeHelpRef} onClick={onCloseHelp} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:hover:bg-slate-800" aria-label="Close help"><X size={19} /></button>
            </header>
            <div className="grid gap-8 p-6 md:grid-cols-[1fr_1.15fr]">
              <div>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Getting started</h3>
                <ol className="mt-4 space-y-4">
                  {[
                    ['1', 'Add components', 'Open the component library and drag icons onto the canvas.'],
                    ['2', 'Connect objects', 'Select an icon and use a blue side handle, or choose the Connector tool.'],
                    ['3', 'Arrange precisely', 'Multi-select objects to align, distribute, or tidy them.'],
                    ['4', 'Edit directly', 'Double-click labels and text. Right-click objects for quick actions.']
                  ].map(([number, title, description]) => (
                    <li key={number} className="flex gap-3">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-700 dark:bg-blue-950 dark:text-blue-300">{number}</span>
                      <div><p className="text-sm font-medium text-slate-800 dark:text-slate-100">{title}</p><p className="mt-0.5 text-xs leading-5 text-slate-500 dark:text-slate-400">{description}</p></div>
                    </li>
                  ))}
                </ol>
                <button type="button" onClick={() => { onCloseHelp(); onOpenLibrary(); }} className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400"><Boxes size={16} /> Open component library</button>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Keyboard shortcuts</h3>
                <dl className="mt-4 divide-y divide-slate-100 rounded-xl border border-slate-200 dark:divide-slate-800 dark:border-slate-700">
                  {shortcuts.map(([keys, action]) => (
                    <div key={keys} className="flex items-center justify-between gap-4 px-3 py-2.5">
                      <dt className="text-xs text-slate-600 dark:text-slate-300">{action}</dt>
                      <dd><kbd className="whitespace-nowrap rounded border border-slate-300 bg-slate-50 px-2 py-1 font-mono text-[11px] text-slate-700 shadow-sm dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200">{keys}</kbd></dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </section>
        </div>
      )}
    </>
  );
};
